'use client';

import { useState, useEffect } from 'react';
import './Chat.css';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Badge,
  Fade,
} from '@mui/material';
import Layout from '../../../components/Layout';
import TeacherNavbar from '../../../components/TeacherNavbar/TeacherNavbar';
import ChatList from '../../../components/Chat/ChatList/ChatList.jsx';
import ChatWindow from '../../../components/Chat/ChatWindow/ChatWindow.jsx';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '../../../assets/ChatIcons/send.svg';
import ChatService from '../../../components/Chat/ChatService/ChatService.jsx';
import ReloadIcon from '../../../assets/ChatIcons/reload-icon.svg';
import LoadingScreen from '../../../components/LoadingIcon/LoadingScreen';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';

const Chat = () => {
  const [chatList, setChatList] = useState([]);
  const [filteredChatList, setFilteredChatList] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'unread'
  const [pageLoading, setPageLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);

  // Fetch chat list on component mount
  useEffect(() => {
    fetchChatList();
  }, []);

  // Fetch chat messages when a chat is selected
  useEffect(() => {
    if (selectedChat) {
      fetchChatMessages(selectedChat.id);
    }
  }, [selectedChat]);

  // Filter chat list when search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredChatList(chatList);
    } else {
      searchStudents(searchTerm);
    }
  }, [searchTerm, chatList]);

  // Filter chat list based on active tab
  useEffect(() => {
    if (activeTab === 'all') {
      setFilteredChatList(chatList);
    } else if (activeTab === 'unread') {
      // Filter chats that have at least one unread message
      const unreadChats = chatList.filter(chat => {
        // Check if any messages for this chat are unread
        return chat.lastMessage && chat.lastMessage.is_read === false;
      });
      setFilteredChatList(unreadChats);
    }
  }, [activeTab, chatList]);

  const fetchChatList = async () => {
    try {
      setLoading(true);
      setPageLoading(true);
      setFadeIn(false);

      const response = await ChatService.getTeacherChatList();

      // Process chat list to determine which chats have unread messages
      const processedChats = response.map(chat => {
        // Check if the last message exists and is unread
        const hasUnreadMessages = chat.lastMessage && chat.lastMessage.is_read === false;
        return {
          ...chat,
          hasUnreadMessages,
        };
      });

      setChatList(processedChats);
      setFilteredChatList(processedChats);
      setLoading(false);
      setPageLoading(false);

      // Delay fade in for a smoother transition
      setTimeout(() => {
        setFadeIn(true);
      }, 100);
    } catch (error) {
      console.error('Error fetching chat list:', error);
      setLoading(false);
      setPageLoading(false);
      setFadeIn(true);
    }
  };

  const fetchChatMessages = async chatId => {
    try {
      setLoading(true);
      const response = await ChatService.getChatMessages(chatId);
      setMessages(response);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching chat messages:', error);
      setLoading(false);
    }
  };

  const handleChatSelect = chat => {
    setSelectedChat(chat);
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
  };

  const searchStudents = async term => {
    try {
      // const results = await ChatService.searchStudents(term);
      await ChatService.searchStudents(term);
      // Filter chat list based on search results
      const filtered = chatList.filter(chat => {
        const name =
          `${chat.recipient_details.first_name} ${chat.recipient_details.last_name}`.toLowerCase();
        return name.includes(term.toLowerCase());
      });
      setFilteredChatList(filtered);
    } catch (error) {
      console.error('Error searching students:', error);
    }
  };

  const handleTabChange = tab => {
    setActiveTab(tab);
  };

  const sendMessage = () => {
    if (!messageInput.trim() || !selectedChat) return;

    // If WebSocket function is available, use it to send the message
    if (handleMessageSent.sendFunction) {
      const tempId = Date.now().toString();
      const success = handleMessageSent.sendFunction(messageInput);

      if (success) {
        // Create a temporary message object for immediate UI feedback
        const tempMessage = {
          id: `temp-${tempId}`,
          message: messageInput,
          sender_id: selectedChat.teacher_id,
          is_sent: true, // Mark as sent
          created_at: new Date().toISOString(),
          temp_id: tempId, // Add a temporary ID to match the server response
          file_url: null,
          file_name: null,
        };

        // Add to messages immediately for UI feedback
        setMessages(prev => [...prev, tempMessage]);

        // Clear input
        setMessageInput('');

        // Ensure chat window scrolls to the latest message
        setTimeout(() => {
          if (handleMessageSent.scrollToBottom) {
            handleMessageSent.scrollToBottom();
          }
        }, 100);
      } else {
        console.error('Failed to send message via WebSocket');
        // Optionally show an error message to the user
      }
    } else {
      console.error('WebSocket send function not available');
      // Optionally show an error message to the user
    }
  };

  const openFilePicker = () => {
    if (handleMessageSent && handleMessageSent.openFilePicker) {
      handleMessageSent.openFilePicker();
    }
  };

  const handleMessageSent = messageData => {
    console.log('Received message data:', messageData);

    // Update messages with the confirmed message from server
    setMessages(prev => {
      // Check if this is a new message or confirmation of a sent message
      const isNewMessageFromOther = messageData.sender_id !== selectedChat?.teacher_id;

      if (isNewMessageFromOther) {
        // Add the new message from the other user
        return [
          ...prev,
          {
            id: messageData.message_id,
            message: messageData.content,
            sender_id: messageData.sender_id,
            recipient_id: messageData.recipient_id,
            is_sent: false, // Received message
            created_at: messageData.created_at || new Date().toISOString(),
            file_url: messageData.file_url,
            file_name: messageData.file_name,
          },
        ];
      } else {
        // For sent message confirmation, find and replace the temp message
        const tempId = messageData.temp_id ? `temp-${messageData.temp_id}` : null;
        let messageFound = false;

        const updatedMessages = prev.map(m => {
          if (tempId && m.id === tempId) {
            messageFound = true;
            return {
              id: messageData.message_id || m.id,
              message: messageData.content || m.message,
              sender_id: messageData.sender_id || m.sender_id,
              recipient_id: messageData.recipient_id || m.recipient_id,
              is_sent: true,
              created_at: messageData.created_at || m.created_at,
              file_url: messageData.file_url || m.file_url,
              file_name: messageData.file_name || m.file_name,
            };
          }
          return m;
        });

        // If the temp message wasn't found, add the new message
        if (!messageFound && (messageData.content || messageData.file_url)) {
          updatedMessages.push({
            id: messageData.message_id,
            message: messageData.content,
            sender_id: messageData.sender_id,
            recipient_id: messageData.recipient_id,
            is_sent: true,
            created_at: messageData.created_at || new Date().toISOString(),
            file_url: messageData.file_url,
            file_name: messageData.file_name,
          });
        }

        return updatedMessages;
      }
    });

    // Force scroll to bottom after receiving server confirmation
    setTimeout(() => {
      if (handleMessageSent.scrollToBottom) {
        handleMessageSent.scrollToBottom();
      }
    }, 100);
  };

  // Add page loading check
  if (pageLoading) {
    return (
      <Layout title="Klass Online | Chat">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white">
            <TeacherNavbar />
            <Box className="h-[calc(100vh-64px)] relative bg-[#F9F9F9] p-4 flex justify-center items-center">
              <LoadingScreen />
            </Box>
          </Box>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Klass Online | Chat">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white">
          <TeacherNavbar />
          <Box className="h-[calc(100vh-64px)] relative bg-[#F9F9F9] p-4 pt-2 pb-2">
            <Fade in={fadeIn} timeout={800}>
              <Box className="flex flex-col gap-2">
                <Typography className="chat-title">All Messages</Typography>

                <Box className="chat-container">
                  {/* Left Column - Chat List */}
                  <Box className="chat-left-column">
                    {/* Search Bar */}
                    <Box className="chat-header">
                      <TextField
                        placeholder="Search"
                        variant="outlined"
                        size="small"
                        className="search-input"
                        value={searchTerm}
                        onChange={handleSearchChange}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ fontSize: 18, color: '#949DA6' }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>

                    {/* Tabs and Chat List */}
                    <Box className="chat-tabs">
                      <Box
                        className={`chat-tab ${activeTab === 'all' ? 'active' : ''}`}
                        onClick={() => handleTabChange('all')}
                      >
                        All
                      </Box>
                      <Box
                        className={`chat-tab ${activeTab === 'unread' ? 'active' : ''}`}
                        onClick={() => handleTabChange('unread')}
                      >
                        Unread
                      </Box>
                      <Box className="chat-actions">
                        <IconButton onClick={() => fetchChatList()} className="chat-reload-button">
                          <Badge color="primary">
                            <img
                              src={ReloadIcon || '/placeholder.svg'}
                              alt="Reload"
                              className="chat-reload-icon"
                            />
                          </Badge>
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Chat List */}
                    <Box className="chat-list-container">
                      {loading && chatList.length === 0 ? (
                        <Box className="loading-container">
                          <LoadingScreen />
                        </Box>
                      ) : (
                        <ChatList
                          chats={filteredChatList}
                          selectedChat={selectedChat}
                          onChatSelect={handleChatSelect}
                        />
                      )}
                    </Box>
                  </Box>

                  {/* Right Column - Chat Window */}
                  <Box className="chat-right-column">
                    {selectedChat ? (
                      <>
                        {/* Chat Header */}
                        <Box className="chat-window-header">
                          <Box className="chat-user-info">
                            {selectedChat.recipient_details.profile_image ? (
                              <img
                                src={
                                  selectedChat.recipient_details.profile_image || '/placeholder.svg'
                                }
                                alt={`${selectedChat.recipient_details.first_name} ${selectedChat.recipient_details.last_name}`}
                                className="chat-user-avatar"
                              />
                            ) : (
                              <Box className="chat-user-avatar-fallback">
                                {selectedChat.recipient_details.first_name.charAt(0)}
                              </Box>
                            )}
                            <Typography className="chat-user-name">
                              {selectedChat.recipient_details.first_name}{' '}
                              {selectedChat.recipient_details.last_name}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Chat Messages */}
                        <ChatWindow
                          messages={messages}
                          loading={loading}
                          selectedChat={selectedChat}
                          onMessageSent={handleMessageSent}
                          recieverName={selectedChat.recipient_details.first_name}
                        />

                        {/* Message Input */}
                        <Box className="chat-input-container">
                          <TextField
                            placeholder="Type your message"
                            variant="outlined"
                            fullWidth
                            value={messageInput}
                            onChange={e => setMessageInput(e.target.value)}
                            onKeyPress={e => {
                              if (e.key === 'Enter') {
                                sendMessage();
                              }
                            }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={openFilePicker}
                                    className="kw-attach-button"
                                    sx={{ mr: 1 }}
                                    aria-label="Attach file"
                                  >
                                    <AttachFileRoundedIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton onClick={sendMessage} className="send-button">
                                    <img src={SendIcon || '/placeholder.svg'} alt="Send" />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                      </>
                    ) : (
                      <Box className="no-chat-selected">
                        <Typography>Select a chat to start messaging</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Box>
      </Box>
    </Layout>
  );
};

export default Chat;
